import { Controller } from '@hotwired/stimulus';
import { Dialog } from 'components/dialog/component';

/**
 * Drives the Contact Sales chrome; Rails and Turbo own the form itself.
 * Shared across subapps: imports nothing from `app/`, `site/`, or `admin/`.
 */
export default class extends Controller {
  static targets = ['frame', 'hiwm', 'fatalTemplate', 'dialog'];
  static values = { handleEvent: Boolean };

  /**
   * Caches the pristine form markup and, when configured, opens on the global
   * `openContactModal` event.
   *
   * @returns {void}
   */
  connect () {
    if (this.hasFrameTarget) this._pristineFrame = this.frameTarget.innerHTML;

    if (this.handleEventValue) {
      this._openListener = () => this.open();
      window.addEventListener('openContactModal', this._openListener);
    }
  }

  /**
   * Tears down the global listeners.
   *
   * @returns {void}
   */
  disconnect () {
    if (this._openListener) window.removeEventListener('openContactModal', this._openListener);
  }

  /**
   * Opens the dialog with a fresh form. Embedded mode renders no dialog.
   *
   * @param {Event} [event]
   * @returns {void}
   */
  open (event) {
    event?.preventDefault();
    if (!this.hasDialogTarget || this.dialogTarget.open) return;

    this._resetForm();
    Dialog.for(this.dialogTarget).open();
  }

  /**
   * Closes the dialog.
   *
   * @param {Event} [event]
   * @returns {void}
   */
  close (event) {
    event?.preventDefault();
    if (this.hasDialogTarget) Dialog.for(this.dialogTarget).close();
  }

  /**
   * Flags that a human interacted with the form, satisfying the server-side
   * `hiwm` check.
   *
   * @returns {void}
   */
  markHuman () {
    this.hiwmTargets.forEach((input) => { input.value = 'yes'; });
  }

  /**
   * Renders the fallback panel when the frame submission fails outside
   * validation (a network error or a 5xx with no matching frame).
   *
   * @param {Event} event
   * @returns {void}
   */
  frameError (event) {
    if (!this.hasFrameTarget || !this.hasFatalTemplateTarget) return;

    event.preventDefault();
    this.frameTarget.replaceChildren(this.fatalTemplateTarget.content.cloneNode(true));
  }

  /**
   * Restores the pristine form markup so every open starts fresh. Skipped when
   * the frame is already pristine, which would needlessly unbind the form.
   *
   * @returns {void}
   */
  _resetForm () {
    if (this.hasFrameTarget && this._pristineFrame !== undefined && this.frameTarget.innerHTML !== this._pristineFrame)
      this.frameTarget.innerHTML = this._pristineFrame;
  }
}
